import { format } from 'date-fns'
import React from 'react'
import {
  BillingDiscounts,
  BillingDiscountsBackendType,
  Feature,
  FeatureBackendType,
  FlatStrategy,
  GenerateInvoiceResponse,
  GenerateInvoiceResponseBackendType,
  InvoiceResponse,
  MerchantInvoice,
  MerchantInvoiceResponseType,
  MerchantPricingFeature,
  MerchantPricingStrategyStatus,
  MeterInvoiceBackendType,
  MeterType,
  NoneStrategy,
  PricingMeter,
  PricingMeterBackendType,
  PricingResponse,
  PricingResponseBackendType,
  PricingStrategyBackendType,
  Stair,
  StairBackendType,
  StairStrategy,
  SubscriptionInvoiceBackendType,
  Tier,
  TierBackendType,
  TierStrategy,
  TransformedTierType
} from '../types/billing'
import { SwitchableTabType } from '../types/random'
import { putRequest } from '../core/api-client'
import { apiURI } from './constants'
import axios from 'axios'

export const parseMerchantInvoices = (invoicesResponse: MerchantInvoiceResponseType): MerchantInvoice => {
  return {
    invoices: invoicesResponse.data.data.map((invoice: InvoiceResponse) => ({
      invoiceNumber: invoice.invoice_number,
      invoiceId: invoice.invoice_id,
      cfSfMerchantId: invoice.cf_sf_merchant_id,
      date: invoice.date,
      dueDate: invoice.due_date,
      invoiceUrl: invoice.invoice_url,
      subTotal: invoice.sub_total,
      taxTotal: invoice.tax_total,
      total: invoice.total,
      currentSubStatus: invoice.current_sub_status,
      paymentLink: invoice.payment_link
    })),
    paginate: {
      totalCount: invoicesResponse.data.total_count,
      pageSize: invoicesResponse.data.page_size,
      maxPages: invoicesResponse.data.max_pages
    }
  }
}

export const parseGenerateInvoiceResponse = (
  generateInvoiceResponse: GenerateInvoiceResponseBackendType
): GenerateInvoiceResponse => {
  return {
    credits: generateInvoiceResponse?.credits ?? 0,
    finalBill: generateInvoiceResponse?.final_bill ?? 0,
    totalBilledAmount: generateInvoiceResponse?.total_billed_amount ?? 0,
    subscriptionInvoice: parseSubscriptionInvoice(generateInvoiceResponse?.subscriptions_invoice) ?? [],
    discounts: parseBillingDiscounts(generateInvoiceResponse?.discounts ?? [])
  }
}

const parseBillingDiscounts = (discounts: BillingDiscountsBackendType[]): BillingDiscounts[] => {
  if (!discounts) return []
  return discounts.map(discount => ({
    amount: discount?.amount ?? 0
  }))
}

export const parseSubscriptionInvoice = (subscriptionInvoice: SubscriptionInvoiceBackendType[]) => {
  if (!Boolean(subscriptionInvoice)) return []
  return subscriptionInvoice.map((invoice: SubscriptionInvoiceBackendType) => ({
    meterInvoice: {
      codOrder: parseMeterInvoice(invoice.meter_invoice.COD_ORDER),
      onlineOrder: parseMeterInvoice(invoice.meter_invoice.ONLINE_ORDER)
    },
    totalBilledAmount: invoice.total_amount
  }))
}

const parseMeterInvoice = (meterInvoice: MeterInvoiceBackendType[]) => {
  if (!Boolean(meterInvoice)) return []
  return meterInvoice.map((invoice: MeterInvoiceBackendType) => ({
    billedAmount: invoice.billed_amount
  }))
}

export const parsePricingResponse = (pricingResponseData: PricingResponseBackendType): PricingResponse => {
  const pricingResponse = pricingResponseData?.data
  const getPrice = (strategy: PricingStrategyBackendType): number => {
    switch (strategy.type) {
      case 'BASE_PRICE':
        return strategy.base_price ?? 0
      case 'MIN_PRICE':
        return strategy.min_amount ?? 0
      default:
        return 0
    }
  }

  const price = getPrice(pricingResponse?.subscription?.strategy ?? { type: 'DRAFT' })

  return {
    subscription: {
      strategy: {
        type: pricingResponse?.subscription?.strategy?.type ?? 'BASE_PRICE',
        basePrice: price
      },
      status: pricingResponse?.subscription?.status ?? MerchantPricingStrategyStatus.ACTIVE
    },
    meters: parsePricingMeters(pricingResponse?.meters) ?? [],
    features: parseFeatures(pricingResponse?.features) ?? []
  }
}

export const parsePricingMeters = (meters: PricingMeterBackendType[]): PricingMeter[] => {
  if (!meters) return []
  return meters.map((meter: any) => ({
    type: meter?.type ?? 'ONLINE_ORDERS',
    strategy: {
      type: meter?.strategy?.type ?? 'FLAT',
      multiplier: meter?.strategy?.multiplier ?? 0,
      stairs: parseStairs(meter?.strategy?.stairs) ?? [],
      tiers: parseTiers(meter?.strategy?.tiers) ?? []
    }
  }))
}

export const parseStairs = (stairs: StairBackendType[]): Stair[] => {
  if (!stairs) return []
  return stairs.map((stair: StairBackendType) => ({
    fixedValue: stair?.fixed_value ?? 0,
    from: stair?.from ?? 0,
    to: stair?.to ?? 0,
    multiplier: stair?.multiplier ?? 0
  }))
}

const parseTiers = (tiers: TierBackendType[]): Tier[] => {
  if (!tiers) return []
  return tiers.map((tier: TierBackendType) => ({
    fixedValue: tier?.fixed_value ?? 0,
    limit: tier?.limit ?? 0,
    multiplier: tier?.multiplier ?? 0
  }))
}

export const parseFeatures = (features: FeatureBackendType[]): Feature[] => {
  if (!features) return []

  return features.map((feature: FeatureBackendType) => {
    return {
      type: feature?.feature_id,
      price: feature?.price,
      discountedPrice: feature?.discounted_price,
      displayName: feature?.display_name ?? feature?.permission?.display_name
    }
  })
}

const METER_TYPE_DESCRIPTIONS: Record<MeterType, string> = {
  [MeterType.ONLINE_ORDER]: 'online orders',
  [MeterType.COD_ORDER]: 'COD orders',
  [MeterType.COD_RETURN]: 'COD returns',
  [MeterType.ONLINE_RETURN]: 'online returns',
  [MeterType.SSO]: 'SSO',
  [MeterType.CART]: 'Cart'
}

const checkoutMeterTypes: MeterType[] = [
  MeterType.COD_ORDER,
  MeterType.ONLINE_ORDER,
  MeterType.COD_RETURN,
  MeterType.ONLINE_RETURN
]

export const hasCheckoutPricing = (meters: PricingMeter[]): boolean => {
  return meters.some(meter => checkoutMeterTypes.includes(meter.type))
}

export const getFlatMeterText = (meterType: MeterType, multiplier: number) => {
  return `${multiplier * 100}% of your ${METER_TYPE_DESCRIPTIONS[meterType]}`
}

const FEATURE_TEXT: Record<MerchantPricingFeature, string> = {
  [MerchantPricingFeature.ACTIVATE_REWARDS]: 'Rewards',
  [MerchantPricingFeature.GROUP_DISCOUNTS]: 'Group Discounts',
  [MerchantPricingFeature.TAB_OVERVIEW]: 'Overview',
  [MerchantPricingFeature.VIEW_OVERVIEW]: 'View Overview',
  [MerchantPricingFeature.DOWNLOAD_REPORT_OVERVIEW]: 'Download Reports',

  // Orders permissions
  [MerchantPricingFeature.TAB_ORDERS]: 'Orders',
  [MerchantPricingFeature.VIEW_ORDERS]: 'View Orders',
  [MerchantPricingFeature.EDIT_ORDERS]: 'Edit Orders',
  [MerchantPricingFeature.CANCEL_ORDERS]: 'Cancel Orders',
  [MerchantPricingFeature.REFUND_ORDERS]: 'Issue Refund',
  [MerchantPricingFeature.EXPORT_ORDER_DATA]: 'Export Orders',
  [MerchantPricingFeature.SHOW_PII_DATA]: 'Show PII Data',

  // Abandoned Checkouts permissions
  [MerchantPricingFeature.TAB_ABANDONED_CHECKOUTS]: 'Abandoned Checkouts',
  [MerchantPricingFeature.VIEW_ABC_TAB]: 'View Abandoned Checkouts',
  [MerchantPricingFeature.EXIT_FORM_CONFIG]: 'Exit Confirmation Config',
  [MerchantPricingFeature.EXPORT_ABC_DATA]: 'Export Data',

  // Upsell permissions
  [MerchantPricingFeature.TAB_UPSELL]: 'Upsell',
  [MerchantPricingFeature.TAB_UPSELL_LITE]: 'Upsell Lite',
  [MerchantPricingFeature.VIEW_UPSELL]: 'View Setup',
  [MerchantPricingFeature.CREATE_UPSELL]: 'Create and Edit',
  [MerchantPricingFeature.EDIT_UPSELL]: 'Create and Edit',
  [MerchantPricingFeature.ENABLE_UPSELL]: 'Enable and Disable',
  [MerchantPricingFeature.DELETE_UPSELL]: 'Delete group',
  [MerchantPricingFeature.VIEW_UPSELL_ANALYTICS]: 'View Analytics',
  [MerchantPricingFeature.DISABLE_UPSELL]: 'Create and Edit',

  // Discounts permissions
  [MerchantPricingFeature.TAB_DISCOUNTS]: 'Discounts',
  [MerchantPricingFeature.VIEW_DISCOUNTS]: 'View Discounts',
  [MerchantPricingFeature.CREATE_DISCOUNTS]: 'Create and Edit',
  [MerchantPricingFeature.ACTIVATE_DISCOUNTS]: 'Activate and Deactivate',
  [MerchantPricingFeature.DELETE_DISCOUNTS]: 'Delete Discounts',

  // Automations permissions
  [MerchantPricingFeature.TAB_AUTOMATIONS]: 'Automations',
  [MerchantPricingFeature.VIEW_AUTOMATIONS]: 'View Automations',
  [MerchantPricingFeature.CREATE_AUTOMATIONS]: 'Create and Edit',
  [MerchantPricingFeature.ACTIVATE_AUTOMATIONS]: 'Activate and Deactivate',
  [MerchantPricingFeature.DELETE_AUTOMATIONS]: 'Delete Automations',

  // Payment Offers permissions
  [MerchantPricingFeature.TAB_PAYMENT_OFFERS]: 'Payment Offers',
  [MerchantPricingFeature.VIEW_PAYMENT_OFFERS]: 'View Payment Offers',
  [MerchantPricingFeature.CREATE_PAYMENT_OFFERS]: 'Create and Edit',
  [MerchantPricingFeature.ACTIVATE_PAYMENT_OFFERS]: 'Activate and Deactivate',
  [MerchantPricingFeature.DELETE_PAYMENT_OFFERS]: 'Delete Payment Offers',

  // Rewards permissions
  [MerchantPricingFeature.TAB_REWARDS]: 'Rewards',
  [MerchantPricingFeature.VIEW_REWARDS_SETUP]: 'View Rewards Setup',
  [MerchantPricingFeature.EDIT_REWARDS_SETUP]: 'Edit Setup',
  [MerchantPricingFeature.ENABLE_REWARDS]: 'Enable and Disable',
  [MerchantPricingFeature.VIEW_REWARDS_CUSTOMISATION]: 'View Customization',
  [MerchantPricingFeature.EDIT_REWARDS_CUSTOMISATION]: 'Edit Customization',
  [MerchantPricingFeature.VIEW_REWARDS_ANALYTICS]: 'View Analytics',

  // Shipping & Delivery permissions
  [MerchantPricingFeature.TAB_SHIPPING_DELIVERY_V2]: 'Shipping Zones',
  [MerchantPricingFeature.VIEW_SHIPPING_DELIVERY]: 'View Shipping Zones',
  [MerchantPricingFeature.EDIT_SHIPPING_DELIVERY]: 'Edit Shipping Zones',
  [MerchantPricingFeature.MANAGE_RATES_VIEW]: 'Manage Rates View',
  [MerchantPricingFeature.MANAGE_RATES_EDIT]: 'Manage Rates Edit',

  // RTO Suite permissions
  [MerchantPricingFeature.TAB_RTO_SUITE]: 'RTO Suite',
  [MerchantPricingFeature.VIEW_RTO_SUITE]: 'View RTO Suite',
  [MerchantPricingFeature.EDIT_RTO_SUITE]: 'Edit RTO Suite',

  // Customise UI permissions
  [MerchantPricingFeature.TAB_CUSTOMISE_UI]: 'Customise UI',
  [MerchantPricingFeature.VIEW_CUSTOMISE_UI]: 'View Customise UI',
  [MerchantPricingFeature.EDIT_CUSTOMISE_UI]: 'Edit Customise UI',
  [MerchantPricingFeature.CUSTOMIZE_BANNER_PAYMENT]: 'Customise Banner on Payments Page',
  [MerchantPricingFeature.CUSTOMIZE_PAYMENT_METHODS]: 'Customise payment methods',
  [MerchantPricingFeature.CUSTOMISE_COUPONS_ADDRESS]: 'Customise Coupons on Address Page',
  [MerchantPricingFeature.CUSTOMISE_COUPONS_LOGIN]: 'Customise Coupons on Login Page',
  [MerchantPricingFeature.CUSTOMIZE_BANNER_ADDRESS]: 'Customise Banner on Address Page',
  [MerchantPricingFeature.CUSTOMIZE_BANNER_LOGIN]: 'Customise Banner on Login Page',
  [MerchantPricingFeature.CUSTOMISE_COUPONS_PAYMENT]: 'Customise Coupons on Payments Page',
  [MerchantPricingFeature.TRUST_BADGES]: 'Edit Trust Badges',
  [MerchantPricingFeature.CHANGE_DESCRIPTION]: 'Change description',

  // Cart permissions
  [MerchantPricingFeature.TAB_CART]: 'Cart',
  [MerchantPricingFeature.VIEW_CART]: 'View Cart',
  [MerchantPricingFeature.EDIT_CART]: 'Edit Cart',

  // Settings permissions
  [MerchantPricingFeature.TAB_SETTINGS]: 'Settings',
  [MerchantPricingFeature.VIEW_SETTINGS]: 'View Settings',
  [MerchantPricingFeature.EDIT_SETUP_SETTINGS]: 'Edit Setup',
  [MerchantPricingFeature.EDIT_SHOP_SETTINGS]: 'Edit Shop Settings',
  [MerchantPricingFeature.EDIT_ACCOUNTS_SETTINGS]: 'Edit Accounts',
  [MerchantPricingFeature.MULTIPLE_FB_CAPI]: 'Multiple FB Cappiing',
  [MerchantPricingFeature.MANAGE_INVOICE]: 'Manage invoice',
  [MerchantPricingFeature.AUTO_PG_ROUTING]: 'PG Auto-routing',
  [MerchantPricingFeature.ADD_CUSTOM_ROLE]: 'Adding custom role',
  [MerchantPricingFeature.MULTIPLE_PG_SUPPORT]: 'Multiple PG allowed',
  [MerchantPricingFeature.CHANNEL]: 'Channel',
  [MerchantPricingFeature.ACR_EVENT_WEBHOOK]: 'ACR webhook',

  // Support permissions
  [MerchantPricingFeature.TAB_SUPPORT]: 'Support',

  // Partners permissions
  [MerchantPricingFeature.TAB_PARTNERS]: 'Partners',

  // One Click Addon permissions
  [MerchantPricingFeature.TAB_ONE_CLICK_ADDON]: 'One-click add on',
  [MerchantPricingFeature.VIEW_ONE_CLICK_ADDON]: 'View One-click add on',
  [MerchantPricingFeature.EDIT_ONE_CLICK_ADDON]: 'Edit One-click add on',
  [MerchantPricingFeature.DELETE_ONE_CLICK_ADDON]: 'Delete One-click add on',
  [MerchantPricingFeature.DISABLE_ONE_CLICK_ADDON]: 'Disable One-click add on',

  // App Store permissions
  [MerchantPricingFeature.TAB_APP_STORE]: 'Shopflo App Store',
  [MerchantPricingFeature.APP_STORE_VIEW]: 'View Shopflo App Store',
  [MerchantPricingFeature.APP_STORE_INSTALL]: 'Install Shopflo Apps',
  [MerchantPricingFeature.APP_STORE_UNINSTALL]: 'Uninstall Shopflo Apps',

  // SSO permissions
  [MerchantPricingFeature.TAB_SSO]: 'SSO',
  [MerchantPricingFeature.VIEW_SSO_SETUP]: 'View SSO Setup',
  [MerchantPricingFeature.VIEW_SSO_ANALYTICS]: 'View SSO Analytics',
  [MerchantPricingFeature.SHOPPASS_REPORT_TAB]: 'Shop Pass Report Tab',

  // Reports permissions
  [MerchantPricingFeature.TAB_REPORTS]: 'Reports',
  [MerchantPricingFeature.REPORTS_ORDERS]: 'Reports orders tab',
  [MerchantPricingFeature.REPORTS_ACR]: 'Reports abandoned checkouts tab',
  [MerchantPricingFeature.REPORTS_CONVERSION_FUNNEL]: 'Reports conversion funnel tab',
  [MerchantPricingFeature.REPORTS_DISCOUNTS]: 'Reports discounts tab',

  // Download Reports permissions
  [MerchantPricingFeature.TAB_DOWNLOAD_REPORTS]: 'Download Reports',

  // Analytics permissions
  [MerchantPricingFeature.TAB_ANALYTICS]: 'Analytics Tab',

  // Testing permissions
  [MerchantPricingFeature.TESTING_PERMISSION]: 'Permission To Test Merchant Specific Permission'
} as const

export const getFeatureText = (featureName: MerchantPricingFeature): string => {
  return FEATURE_TEXT[featureName]
}

export const getAllFeatureText = (features: Feature[]) => {
  const uniqueFeatures = Array.from(new Set(features.map(feature => feature.type)))
  return uniqueFeatures.map(featureType => getFeatureText(featureType)).join(', ')
}

export const nextBillingDate = () => {
  const today = new Date()
  const currentDay = today.getDate()

  const nextBillingDay = new Date(today.getFullYear(), currentDay === 1 ? today.getMonth() : today.getMonth() + 1, 2)

  return format(nextBillingDay, 'MMM d, yyyy')
}

export const billingPeriod = () => {
  const today = new Date()
  const currentDay = today.getDate()

  const startDate = new Date(today.getFullYear(), currentDay < 2 ? today.getMonth() - 1 : today.getMonth(), 1)

  return `Billed on: ${format(startDate, 'MMM d, yyyy')} - ${format(today, 'MMM d, yyyy')}`
}

export const defaultBillingTabList: SwitchableTabType[] = [
  {
    name: 'Subscriptions',
    label: 'Subscription',
    isActive: false,
    childComponent: React.createElement('div')
  },
  {
    name: 'Invoices',
    label: 'Invoices',
    isActive: false,
    childComponent: React.createElement('div')
  }
]

export const getActiveBillingTab = (tabList: SwitchableTabType[]) => {
  const hash = window.location.hash.replace('#', '').toLowerCase()

  if (!hash) {
    return tabList.map((tab, index) => ({
      ...tab,
      isActive: index === 0
    }))
  }

  return tabList.map(tab => ({
    ...tab,
    isActive: tab.name.toLowerCase() === hash
  }))
}

export const getShoppassPricing = (pricingResponse: PricingResponse) => {
  const shoppassPricing = pricingResponse.meters.find(meter => meter.type === MeterType.SSO)
  return (
    shoppassPricing ?? {
      type: MeterType.SSO,
      strategy: {
        type: 'NONE',
        stairs: []
      }
    }
  )
}

export const getCartPricing = (pricingResponse: PricingResponse) => {
  const cartPricing = pricingResponse.meters.find(meter => meter.type === MeterType.CART)
  return (
    cartPricing ?? {
      type: MeterType.CART,
      strategy: {
        type: 'NONE',
        multiplier: 0,
        tiers: []
      }
    }
  )
}

export const getFlatMeter = (type: MeterType, meters: PricingMeter[]) =>
  meters.find(meter => meter.type === type && meter.strategy.type === 'FLAT')

export const isFlatStrategy = (
  strategy: FlatStrategy | StairStrategy | TierStrategy | NoneStrategy
): strategy is FlatStrategy => {
  return strategy.type === 'FLAT'
}

export const generateTierData = (data: Tier[]): TransformedTierType[] => {
  if (!data || data.some(item => !item.limit)) return []

  const sortedData = [...data].sort((a, b) => a.limit - b.limit)

  const result: TransformedTierType[] = []
  let previousLimit = 0

  sortedData.forEach((tier, index) => {
    const minOrder = previousLimit + 1
    const maxOrder = tier.limit

    result.push({
      minOrder,
      maxOrder,
      flatPrice: tier.fixedValue
    })

    previousLimit = tier.limit
  })

  return result
}

export const updateOrganizationStatus = async (
  status: 'DISABLED' | 'CHURN_REVIEW' | 'LIVE',
  organizationId: string
) => {
  const response = await putRequest(`/organization/update-shop-churn-status`, apiURI.AUTH_ENGINE, {
    merchant_id: organizationId,
    status: status
  })
  return response
}

export const fetchCalendlyDetails = async (eventUri: string, authToken?: string) => {
  try {
    const res = await axios.get(eventUri, {
      headers: {
        Authorization: `Bearer ${authToken}`,
        'Content-Type': 'application/json'
      }
    })

    return res.data?.resource
  } catch (err) {
    console.error('Failed to fetch Calendly details:', err)
    throw err // optional: rethrow for caller to handle
  }
}
